import i18next from 'i18next';
import * as THREE from 'three';
import {
  Box2,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  FrontSide,
  Intersection,
  Mesh,
  Object3D,
  Points,
  Raycaster,
  Vector2,
  Vector3,
} from 'three';

import {
  ContextMenuItem,
  SkyViewContextMenuProps,
} from '../../react/components/skyview/context-menu';
import { PointHeightInterpolationMode } from '../../react/components/skyview/right-menu/tools/design-tool-menu/features/interpolate-point-heights';
import { DesignFeature } from '../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { getNextDefaultName } from '../../react/hooks/use-default-asset-name';
import { setPageHasUnsavedChangesGlobal } from '../../react/state/unsaved-changes-bridge';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { FeatureFlagsManager } from '../../utils/core/feature-flags-manager';
import { localStorageUtils } from '../../utils/local-storage-utils';
import { roundToDecimals } from '../../utils/math/number';
import { vectorToSlopePercent } from '../../utils/math/slope';
import { isDefined } from '../../utils/variables';
import { AssetManager } from '../asset-manager';
import { AssetVisibilityManager } from '../asset-visibility-manager';
import cameraManager from '../camera-manager';
import { SkyviewCamera } from '../cameras/types';
import { CoordinateManager } from '../coordinate-manager';
import { Design } from '../elements/design';
import { getDefaultColorValueMap } from '../elements/design/colors';
import {
  getCenterCircles,
  getCircleHandles,
  getLineSegmentCircle,
  getSeamCircle,
  isHiddenSeam,
  withWholeCircles,
} from '../elements/design/design-node/utils/circle';
import {
  Conic,
  getConicDirection,
  getConicParameter,
} from '../elements/design/design-node/utils/conic';
import { FaceId, findFaceIdFromIntersection } from '../elements/design/design-node/utils/face-node';
import {
  CrossingIntersection,
  findLineSegmentIdFromIntersection,
  getLineSegmentFaces,
  isLineSegmentObject,
  LineSegmentId,
} from '../elements/design/design-node/utils/line-segment-node';
import {
  findPointIdFromIntersection,
  PointId,
} from '../elements/design/design-node/utils/point-node';
import { AttributeData, AttributeGroupEntry, AttributeId } from '../elements/design/skymap-extras';
import { isStyledLines } from '../elements/styled-lines/styled-lines';
import { PointCloudHandle } from '../geodata/point-clouds/point-cloud-handle';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import {
  KeyInputData,
  MouseButtonType,
  MouseEventType,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import {
  clamp,
  coordinateToPoint3d,
  degreesToRadians,
  findPointOnLineFromHeight,
  isValidVector3,
  Point2d,
} from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { ViewSettings } from '../view-settings';
import { CrossedLine, PickingAngle } from './picking-angle';
import { PickingFace } from './picking-face';
import { PickingIncline } from './picking-incline';
import { PickingLine } from './picking-line';
import { PickingSphere } from './picking-sphere';
import { isAssetPickable } from './select-tool';
import { Tool } from './tool';
import { buildTooltipProps, MenuTooltipTool, showToolTooltip } from './tooltip-helper';
import { getAngleGuide } from './utils/angle-guide';
import {
  AssetLine,
  getAssetLine,
  getAssetLineReference,
  getAssetLineTarget,
  getIntersectedLineColor,
} from './utils/asset-line';
import { copyCoordinate, option } from './utils/context-menu/commands';
import { designToolCommands as commands } from './utils/context-menu/design-tool/commands';
import { breakCircle, CircleHandle } from './utils/context-menu/design-tool/commands/circle/edit';
import {
  abortActionInProgress,
  quickSelectLineOrPolygon,
} from './utils/context-menu/design-tool/commands/general';
import { ClearAttribute } from './utils/context-menu/design-tool/utils/attributes';
import {
  CurveDistanceInput,
  CurveMeasure,
  getLineSegmentConic,
  getLineSegmentCursorVertices,
  getLineSegmentMiddle,
} from './utils/context-menu/design-tool/utils/curve';
import {
  deselect,
  getMultiselectResult,
  SelectionResult,
} from './utils/context-menu/design-tool/utils/select';
import { HeightLayersGuide } from './utils/height-layers';
import {
  applyLineConstraints,
  getAnglesToLine,
  LineConstraints,
  LineReference,
  perpendicularAngles,
  snapDirectionToAngle,
} from './utils/line-constraints';
import { CrossingLine, findClosestCrossing, getHeightOnLine } from './utils/line-crossing';
import {
  CenterCurveHighlight,
  chooseSnapCandidate,
  findAssetSnapCandidates,
  getAssetCrossingLines,
  getRayCursor,
  maxCrossingLines,
  SnapCandidate,
} from './utils/snap-candidates';
import {
  getSnapSettingsStatus,
  SnapAction,
  TooltipSnapSettings,
} from './utils/snap-settings-status';
import { getLineSnapStylePoint, SnapStyle, SnapStyleOption } from './utils/snap-style';
import { snapToTrajectory, TrajectorySnapTarget } from './utils/snap-to-trajectory';

export enum SnapMode {
  Timeline = 'Timeline',
  AbsoluteHeight = 'AbsoluteHeight',
  RelativeHeight = 'RelativeHeight',
  RelativeSlopePercent = 'RelativeSlope',
  RelativeSlopeFraction = 'RelativeSlopeFraction',
}

// Yellow
const defaultCursorColor = 0xffff00;
// Green
const snapColor = new Color(0, 1, 0);
// Light blue
const curveHandleColor = 0x40a0ff;
/** Size of the curve handle on screen (pixels). */
const curveHandleSizePx = 12;

export enum PlaceMode {
  /** Point will be placed where cursor is snapping to */
  Free = 'Free',
  /** Point will be placed in a perpendicular angle based on last two points */
  FreePerpendicular = 'FreePerpendicular',
  /** Point will be placed a certain distance from last point */
  Length = 'Length',
  /** Point will be placed a certain distance from last point in a perpendicular angle based on last two points */
  LengthPerpendicular = 'LengthPerpendicular',
  /** Point will be placed at specific position */
  Position = 'Position',
  /** Point will be placed at a position relative to last point */
  OffsetXYZ = 'OffsetXYZ',
  /** Point will be intersected towards the pointcloud */
  IntersectPointcloud = 'IntersectPointcloud',
  /** Point will be intersected towards objects in the scene */
  IntersectObjects = 'IntersectObjects',
}

export interface EditAction {
  commit: () => void;
  rollback: () => void;
}

export enum ActionName {
  CreateLine,
  CreateText,
  MovePoint,
  Triangulate,
  Offset,
  InterpolatePointHeights,
  SelectPolyLine,
  SetHeight,
  CreateCurve,
  BendCurve,
  CreateCircle,
  ResizeCircle,
}

export interface ActionInProgress {
  actionName: ActionName;
  initiate?: () => void;
  cleanup?: () => void;
  refresh?: () => void;
  refreshInProgress?: boolean;
  apply: (e?: MouseInputData) => void;
  readyToApply: () => boolean;
  onProjectProjectionUpdated?: (origoDiff: Vector3) => void;
  onMouseUp?: (e: MouseInputData) => void;
  getExtraOptions?: (e: MouseInputData) => ContextMenuItem[];
  origin?: Vector3;
  originPointId?: PointId;
  originLineSegmentId?: LineSegmentId;
  /** The line segment that originLineSegmentId crosses at the origin, see CrossingIntersection. */
  originCrossedLineSegmentId?: LineSegmentId;
  /**
   * The line of another asset that the action starts on, e.g. of a CAD drawing. Angles are measured
   * from it when the action doesn't start on the geometry of the design.
   */
  originAssetLine?: AssetLine;
  /**
   * What the tooltip is about while the cursor isn't snapped to anything, e.g. the vertex of the
   * offset closest to the cursor, which follows the cursor all the same. With it, the tooltip stays
   * shown during the action.
   */
  tooltipPoint?: () => Vector3 | undefined;
}

/**
 * Determines the behaviour when left clicking in the scene when the tool is active.
 */
export enum DesignMode {
  Line,
  Point,
  Select,
  Text,
  Curve,
  Circle,
}

export enum DesignToolDialogState {
  None = 'None',
  Saving = 'Saving',
  Abort = 'Abort',
  SetName = 'SetName',
  CreatePointByCoordinate = 'CreatePointByCoordinate',
  CreatePointByOffset = 'CreatePointByOffset',
}

type SelectOptions = {
  points: boolean;
  lines: boolean;
  faces: boolean;
  breakLines: boolean;
};

export type SnapValues = {
  height: number;
  /**
   * The z-offset: from the previous point of a line, the line that is offset, or otherwise the
   * surface that is snapped to.
   */
  heightOffset: number;
  length: number;
  slope: number;
  /** Angle in degrees */
  angle: number;
  pointCloud: boolean;
  assets: boolean;
  tiledModel: boolean;
  position: Vector3;
  xyzOffset: Vector3;
};

/**
 * Snap settings that can be combined with each other, used when flexible snapping is enabled.
 * Undefined means that the setting is turned off.
 */
export type SnapSettings = {
  /**
   * The snap styles that are on, several at the same time, see chooseSnapCandidate. A snap style
   * snaps to a point of the geometry near the cursor, of the design or of assets, instead of the
   * hovered position, which free snaps to.
   */
  styles: SnapStyleOption[];
  /** Adjusts the height of the snapped point */
  height?: 'absolute' | 'offset';
  /** Snaps the direction of a line to an angle relative to the previous line */
  angle?: 'perpendicular' | 'custom';
  /**
   * Sets the length of a line, either horizontally (2d), along the line (3d) or until it hits
   * any of the snap targets (cutFill)
   */
  length?: '2d' | '3d' | 'cutFill';
  /** Sets the slope of a line, overrides the height of the snapped point */
  incline?: 'slopePercent' | 'slopeDegrees' | 'slopeRatio';
};

const getDefaultSnapValues = (): SnapValues => ({
  height: 0,
  heightOffset: 0,
  length: 0,
  position: new Vector3(),
  slope: 0,
  angle: 45,
  xyzOffset: new Vector3(),
  assets: true,
  pointCloud: true,
  tiledModel: false,
});

/**
 * The snap settings that each snap mode of the old snapping menu stands for: the height or the
 * incline, one of them.
 */
const snapModeSettings: Record<SnapMode, Partial<SnapSettings>> = {
  [SnapMode.Timeline]: { height: undefined, incline: undefined },
  [SnapMode.AbsoluteHeight]: { height: 'absolute', incline: undefined },
  [SnapMode.RelativeHeight]: { height: 'offset', incline: undefined },
  [SnapMode.RelativeSlopePercent]: { height: undefined, incline: 'slopePercent' },
  [SnapMode.RelativeSlopeFraction]: { height: undefined, incline: 'slopeRatio' },
};

/**
 * The snap settings that each place mode of the old snapping menu stands for: the angle and the
 * length. The length of the old menu is horizontal.
 */
const placeModeSettings: Partial<Record<PlaceMode, Partial<SnapSettings>>> = {
  [PlaceMode.Free]: { angle: undefined, length: undefined },
  [PlaceMode.FreePerpendicular]: { angle: 'perpendicular', length: undefined },
  [PlaceMode.Length]: { angle: undefined, length: '2d' },
  [PlaceMode.LengthPerpendicular]: { angle: 'perpendicular', length: '2d' },
  [PlaceMode.IntersectPointcloud]: { angle: undefined, length: 'cutFill' },
  [PlaceMode.IntersectObjects]: { angle: undefined, length: 'cutFill' },
};

const getDefaultSnapSettings = (): SnapSettings =>
  ViewSettings.instance.isCadViewer
    ? { styles: ['free'], height: 'absolute' }
    : { styles: ['free'] };

/**
 * Gets where the line from start through end reaches the height, or with the opposite slope where
 * it leads away from it, as the surfaces are reached, see Pick.onRay. The height itself is not
 * mirrored: a height above the start is only reached above it. None for a level line, or one that
 * starts at the height, which it would end on.
 */
export function extendToHeight(start: Vector3, end: Vector3, height: number): Vector3 | undefined {
  const direction = end.clone().sub(start);

  for (const z of [direction.z, -direction.z]) {
    const t = (height - start.z) / z;

    if (Number.isFinite(t) && t > 1e-9) {
      return start.clone().addScaledVector(direction.clone().setZ(z), t);
    }
  }

  return undefined;
}

/**
 * A point of the design that a snap style targets, with what it is on, see getSnapStyleTarget.
 */
type DesignSnapCandidate = SnapCandidate & { intersection?: Intersection };

export class DesignTool extends Tool {
  /**
   * Sets the place mode of the old snapping menu, as the snap settings it stands for, see placeMode.
   * The coordinate and the offset XYZ are not place modes of the snap settings, see CreatePoints.
   */
  onPlaceModeChanged(value: PlaceMode) {
    this.onSnapSettingsChanged(placeModeSettings[value] ?? {});
  }
  onSnapValuesChanged(newValue: Partial<SnapValues>) {
    this.snapValues = {
      ...this.snapValues,
      ...newValue,
    };
    this.currentPoint = undefined;
  }
  /**
   * Sets the snap mode of the old snapping menu, as the snap settings it stands for, see snapMode.
   */
  onSnapModeChanged(value: SnapMode) {
    this.onSnapSettingsChanged(snapModeSettings[value]);
  }
  /**
   * Assets that are not snapped towards, by uuid.
   */
  excludedSnapAssetIds = new Set<string>();
  onExcludedSnapAssetsChanged(assetIds: Set<string>) {
    this.excludedSnapAssetIds = new Set(assetIds);
    this.currentPoint = undefined;
  }
  /**
   * Whether an asset is snapped towards.
   */
  isSnapAsset = (asset: Object3D) =>
    isAssetPickable(asset) && !this.excludedSnapAssetIds.has(asset.uuid);
  onSnapSettingsChanged(newValue: Partial<SnapSettings>) {
    this.snapSettings = {
      ...this.snapSettings,
      ...newValue,
    };
    this.currentPoint = undefined;
  }
  private chosenCurveMeasure?: CurveMeasure;
  /**
   * How the size of curves is typed, the radius unless the user chose the bulge, which is remembered.
   */
  get curveMeasure(): CurveMeasure {
    return (
      this.chosenCurveMeasure ??
      (localStorageUtils.getUserItemOrDefault('designToolCurveMeasure', 'radius') === 'bulge'
        ? 'bulge'
        : 'radius')
    );
  }
  set curveMeasure(measure: CurveMeasure) {
    this.chosenCurveMeasure = measure;
    localStorageUtils.setUserItem('designToolCurveMeasure', measure);
  }
  /**
   * The snap styles that are on, without free.
   */
  private get snapStyles(): SnapStyle[] {
    return this.snapSettings.styles.filter((x): x is SnapStyle => x !== 'free');
  }
  /**
   * Whether the hovered position is snapped to, when no point of a snap style is near the cursor.
   */
  private get freeSnapping() {
    return this.snapSettings.styles.includes('free');
  }
  get designCurves() {
    return FeatureFlagsManager.instance.isEnabled('SK-7535-design-curves');
  }
  /**
   * The snap mode of the old snapping menu that matches the snap settings, which the menu sets,
   * see onSnapModeChanged. Also for features that only have these modes, e.g. offsets.
   */
  get snapMode(): SnapMode {
    switch (this.snapSettings.incline) {
      case 'slopePercent':
      case 'slopeDegrees':
        return SnapMode.RelativeSlopePercent;
      case 'slopeRatio':
        return SnapMode.RelativeSlopeFraction;
    }

    switch (this.snapSettings.height) {
      case 'absolute':
        return SnapMode.AbsoluteHeight;
      case 'offset':
        return SnapMode.RelativeHeight;
      default:
        return SnapMode.Timeline;
    }
  }
  set snapMode(value: SnapMode) {
    this.onSnapModeChanged(value);
  }

  /**
   * Whether the point cloud is snapped towards: when it is chosen and there is one, which there is
   * not in the CAD viewer, where it can't be chosen.
   */
  get snapsToPointCloud(): boolean {
    return (
      this.snapValues.pointCloud &&
      !ViewSettings.instance.isCadViewer &&
      PointCloudHandle.instance.available()
    );
  }

  /**
   * The place mode of the old snapping menu that matches the snap settings, which the menu sets,
   * see onPlaceModeChanged. Also for features that only have these modes, e.g. offsets.
   */
  get placeMode(): PlaceMode {
    const perpendicular = this.snapSettings.angle === 'perpendicular';

    switch (this.snapSettings.length) {
      case '2d':
      case '3d':
        return perpendicular ? PlaceMode.LengthPerpendicular : PlaceMode.Length;
      case 'cutFill':
        // The offset loads the point cloud in high resolution when it is one of the snap targets.
        return this.snapsToPointCloud ? PlaceMode.IntersectPointcloud : PlaceMode.IntersectObjects;
      default:
        return perpendicular ? PlaceMode.FreePerpendicular : PlaceMode.Free;
    }
  }
  set placeMode(value: PlaceMode) {
    this.onPlaceModeChanged(value);
  }
  onCancelTool(): void {
    this.saved = true;
    this.inputHandler.setTool('select');
  }
  onEdited?: () => void;
  onSaved?: () => void;
  private _activeFeature = DesignFeature.SetPointHeights;
  get activeFeature() {
    return this._activeFeature;
  }
  set activeFeature(newValue: DesignFeature) {
    this._activeFeature = newValue;
    publish(SubscriptionTopic.DesignToolActiveFeatureChanged, undefined);
  }
  private toolCursor: 'crosshair' | 'default' = 'crosshair';
  private mouseInView = false;

  onDesignModeChanged?: (newValue: DesignMode) => void;
  private _activeTool = DesignMode.Line;
  set designMode(newValue: DesignMode) {
    if (this.actionInProgress) {
      void abortActionInProgress(this).action();
    }

    this._activeTool = newValue;
    this.curveHandle.visible = false;
    this.hoveredCurveHandle = undefined;
    this.hideCircleHandles();
    this.onDesignModeChanged?.(newValue);
    this.toolCursor = newValue === DesignMode.Select ? 'default' : 'crosshair';
    this.inputHandler.setCursor(this.toolCursor);
  }
  get designMode() {
    return this._activeTool;
  }

  async onInitiate(design: Design) {
    this.asset = design;
    this.asset.locked = true;
    this.asset.setPointSize(true);

    if (!design.assetId) {
      this.asset.onSelect();
      AssetManager.instance.addTemp(this.asset);
    } else {
      this.asset.opacity = 1;

      if (!this.asset.getStateVisibility()) {
        await AssetVisibilityManager.getInstance().toggleAsset(this.asset.uuid, true, false);
      }
    }

    this.asset.toggleDoubleSided(FrontSide);
    publish(SubscriptionTopic.DesignAttributesChanged, undefined);
    publish(SubscriptionTopic.DesignToolActiveActionChanged, undefined);
    publish(SubscriptionTopic.DesignToolActiveFeatureChanged, undefined);
    publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
  }
  onSaveDialogSaved() {
    this.onSaved?.();
    this.onDialogClose();
    AssetManager.instance.select(this.asset);
    AssetManager.instance.removeTemp(this.asset, { dispose: false });
  }

  async onSaveDialogDiscardChanges() {
    // Reload model data if the changes are discared.
    if (this.asset?.loadingStatus === 'loaded') {
      await this.asset.downloadAndLoadModel();
    }

    this.saved = true;
    this.inputHandler.setTool('select');
  }

  onDialogClose() {
    this.uiState = DesignToolDialogState.None;
  }

  /**
   * Value which is used for calculation during snapping
   * Can be height or angle which depends on snap mode.
   */
  snapValues: SnapValues = getDefaultSnapValues();
  snapSettings: SnapSettings = getDefaultSnapSettings();
  /**
   * Highlighted point.
   */
  cursorPoint: PickingSphere;
  /**
   * Highlighted line.
   */
  cursorLine: PickingLine;
  /**
   * Highlighted face.
   */
  cursorFace: PickingFace;
  /**
   * Handle in the middle of the hovered line segment in curve mode. The cursor snaps to it, and
   * pressing M bends the line segment, like moving a point.
   */
  curveHandle: PickingSphere;
  /**
   * The line segment whose curve handle the cursor is on.
   */
  hoveredCurveHandle?: LineSegmentId;
  /**
   * Handles of the hovered circle or ellipse: the ends of its axes (squares), and the middles of
   * its arcs (rounds). The cursor snaps to them, pressing M changes the shape, and clicking one
   * breaks the circle into arcs and points, to create geometry there.
   */
  circleHandles: { corners: PickingSphere[]; middles: PickingSphere[] };
  /**
   * The handle of a circle that the cursor is on.
   */
  hoveredCircleHandle?: CircleHandle;
  /**
   * Shows the reference line and the angle when a line is snapped to an angle.
   */
  angleGuide: PickingAngle;
  /**
   * Shows which way is uphill on the line being created or the hovered line.
   */
  inclineGuide: PickingIncline;
  /**
   * Geometry that represents points selected with multiselect.
   */
  selectedPoints: PickingSphere;
  /**
   * Geometry that represents lines selected with multiselect.
   */
  selectedLineSegments: PickingLine;
  /**
   * Geometry that represents faces selected with multiselect.
   */
  selectedFaces: PickingFace;
  selectionBox: PickingFace;

  /**
   * A list of attributes to be applied to new points or updated points
   */
  activeAttributes = new Map<AttributeId, AttributeData & ClearAttribute>();
  activeAttributeGroup?: { name: string; entries: AttributeGroupEntry[] };
  activeAttributeGroupRowId?: string;
  applyAttributesOnNewPoints = false;
  autoTriggerOffset = false;
  pointHeightInterpolationMode: PointHeightInterpolationMode = 'interpolateStartEnd';
  pointHeightInterpolationSlope = 0;
  autoTriggerTriangulation = false;
  setPointHeightsValueNeedsUpdate = false;

  currentSelection: SelectionResult = { points: [], lineSegments: [], faces: [] };
  private _selectionOptions: SelectOptions = {
    points: true,
    lines: true,
    breakLines: true,
    faces: true,
  };
  get selectOptions() {
    return this._selectionOptions;
  }
  onSelectOptionsChanged?: (newOptions: SelectOptions) => void;

  getActiveAttributes(): Map<AttributeId, AttributeData> {
    if (!this.applyAttributesOnNewPoints) {
      return new Map();
    }

    return new Map([...this.activeAttributes.entries()].filter((x) => !x[1].clearAttribute));
  }

  setSelectOptions(newOptions: Partial<SelectOptions>) {
    this._selectionOptions = {
      ...this._selectionOptions,
      ...newOptions,
    };
    this.onSelectOptionsChanged?.(this._selectionOptions);
  }

  private selectionStartPoint = new Vector2();
  private selectionEndPoint = new Vector2();

  /**
   * How many pixels away from the cursor should it be possible to snap.
   * Should be large enough so its not difficult to snap, but small enough
   * so that it doesn's snap when its not supposed to.
   */
  private pixelSnapDistance = 20;
  private minGeometryDistanceFromCamera = 0.1;
  private maxGeometryDistanceFromCamera = 350;

  private _actionInProgress?: ActionInProgress;
  get actionInProgress() {
    return this._actionInProgress;
  }

  set actionInProgress(value: ActionInProgress | undefined) {
    this._actionInProgress = value;
    // The guides belong to the previous action, they are shown again when the pointer is placed.
    this.angleGuide.hide();
    this.inclineGuide.hide();
  }

  actions: EditAction[] = [];
  undoActions: EditAction[] = [];
  intersection?: Intersection;
  /**
   * The line of another asset that the cursor snaps to, e.g. of a CAD drawing.
   */
  assetLine?: AssetLine;
  /**
   * The distance a curve being bent pokes out, shown in the tooltip where it can be typed.
   */
  curveDistanceInput?: CurveDistanceInput;
  /**
   * The line that the current point is placed on when a line snapped to an angle ends where it crosses a line.
   */
  private crossedLine?: CrossedLine;
  /**
   * The curves whose center the current point is snapped to, highlighted by centerHighlight.
   */
  private centerCurves?: Conic[];
  readonly centerHighlight = new CenterCurveHighlight();
  /**
   * All heights at the snapped point, while its option is on.
   */
  readonly heightsGuide = new HeightLayersGuide();

  /**
   * The world position of where the cursor is.
   */
  currentPoint: THREE.Vector3 | undefined;

  asset: Design = this.getDefaultGeometry();

  private _saved = true;
  get saved() {
    return this._saved;
  }

  set saved(value: boolean) {
    this._saved = value;
    setPageHasUnsavedChangesGlobal(!value);
  }

  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  /** Whether the tooltip shows the snap settings, see getTooltipSnapSettings. */
  tooltipSnapSettings = true;
  /**
   * Set while the tooltip is shown in the right-side menu, which then gets it instead of the cursor.
   */
  onMenuTooltipChanged?: MenuTooltipTool['onMenuTooltipChanged'];
  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;

  private _uiState: DesignToolDialogState = DesignToolDialogState.None;
  /**
   * The world position the create point dialogs start from. For the offset dialog it is the
   * position the offset is relative to, for the coordinate dialog it is the coordinate to start with.
   */
  pointDialogOrigin?: Vector3;
  onStateChanged?: () => void;

  get uiState() {
    return this._uiState;
  }

  set uiState(value: DesignToolDialogState) {
    this._uiState = value;
    this.onStateChanged?.();
  }

  constructor(inputHandler: InputHandler) {
    super('design', inputHandler);

    this.visible = false;

    this.cursorPoint = new PickingSphere(new THREE.Color(defaultCursorColor));
    this.add(this.cursorPoint);

    this.cursorLine = new PickingLine();
    this.add(this.cursorLine);
    this.add(this.centerHighlight);
    this.add(this.heightsGuide);

    this.cursorFace = new PickingFace();
    this.add(this.cursorFace);

    this.curveHandle = new PickingSphere(new Color(curveHandleColor), curveHandleSizePx, 'square');
    this.curveHandle.visible = false;
    this.add(this.curveHandle);

    const createCircleHandle = (style: 'square' | 'round') => {
      const handle = new PickingSphere(new Color(curveHandleColor), curveHandleSizePx, style);
      handle.visible = false;
      this.add(handle);
      return handle;
    };
    this.circleHandles = {
      corners: Array.from({ length: 4 }, () => createCircleHandle('square')),
      middles: Array.from({ length: 4 }, () => createCircleHandle('round')),
    };

    this.angleGuide = new PickingAngle();
    this.add(this.angleGuide);

    this.inclineGuide = new PickingIncline();
    this.add(this.inclineGuide);

    this.selectedPoints = new PickingSphere();
    this.selectedPoints.visible = false;
    this.selectedPoints.setIndicesAndVertices(new Float32BufferAttribute([], 3), []);
    this.add(this.selectedPoints);

    this.selectedLineSegments = new PickingLine();
    this.selectedLineSegments.visible = false;
    this.add(this.selectedLineSegments);

    this.selectedFaces = new PickingFace();
    this.selectedFaces.visible = false;
    this.add(this.selectedFaces);

    this.selectionBox = new PickingFace(new Color(0.2, 0.2, 1));
    this.selectionBox.visible = false;
    this.add(this.selectionBox);
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  getMeta(sweref?: string): MetaDefinition {
    return {
      src: 'tool.design',
      parameters: undefined,
    };
  }

  // OBS WARN Touch är INTE implementerat med all funktionalitet.
  onTouchStart(e: TouchInputData): boolean {
    return this.onTouchMove(e);
  }

  onTouchMove(e: TouchInputData) {
    return true;
  }

  /**
   * Tries to find a snap point and return the world position for it.
   */
  findSnapPoint(
    e: MouseInputData,
    camera: SkyviewCamera,
    intersectAgainstSelf = true,
    basePoint?: Vector3,
  ): Vector3 | undefined {
    /**
     * Returns undefined if vector contains NaN values.
     */
    const getSafeVector3 = (v?: Vector3) => (isValidVector3(v) ? v : undefined);

    this.crossedLine = undefined;
    this.assetLine = undefined;
    this.centerCurves = undefined;

    const rayCaster = camera.getRay(e.pointer.position.x, e.pointer.position.y);

    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      this.pixelSnapDistance,
      clamp(
        this.cursorPoint.position.distanceTo(rayCaster.ray.origin),
        this.minGeometryDistanceFromCamera,
        this.maxGeometryDistanceFromCamera,
      ),
    );

    const getClosestToRay = (...objects: Object3D[]) => {
      // Recursive, since styled lines are drawn by the children of StyledLines.
      const intersections = rayCaster.intersectObjects(objects, true);

      if (intersections.length === 0) {
        return;
      }

      // Recalculate distanceToRay based on pixels on the screen.
      for (const intersection of intersections) {
        if (intersection.object instanceof Points) {
          intersection.point = this.asset.designNode.getVertexByIndex(intersection.index!, 'world');
        }

        const a = camera.getScreenPosition(intersection.point);
        const b = e.pointer.client;
        intersection.distanceToRay = a.distanceTo(b);
      }

      // THREE js already sort based on distance to camera, so the first intersection is the closest
      const closestToRay = intersections.reduce((prev: Intersection, cur: Intersection) => {
        return cur.distanceToRay! < prev.distanceToRay! ? cur : prev;
      }, intersections[0]);

      if (!closestToRay) {
        return;
      }

      return closestToRay.distanceToRay! < this.pixelSnapDistance ? closestToRay : undefined;
    };

    if (intersectAgainstSelf) {
      /**
       * Get the closest point with priority
       * 1. Points
       * 2. Lines
       * 3. Mesh
       */
      const node = this.asset.designNode;
      // The points first, then the lines, then the faces.
      const getIntersections = [
        () => getClosestToRay(node.data.points),
        () => getClosestToRay(node.data.lineSegments, node.data.styledLines),
        () => getClosestToRay(node.data.faces),
      ];
      const intersections: Intersection[] = [];

      for (const getIntersection of getIntersections) {
        const intersection = getIntersection();

        if (intersection) {
          intersections.push(intersection);

          // Without snap styles, only the closest geometry is snapped to.
          if (this.snapStyles.length === 0) {
            break;
          }
        }
      }

      const closestIntersection = intersections[0];
      // The point that the snap styles target instead of the hovered position, on the geometry
      // (e.g. an end point or a line segment) unless it is off it (the center of a curve).
      const styled = chooseSnapCandidate(
        this.getDesignSnapCandidates(camera, rayCaster, intersections),
        this.freeSnapping,
        this.pixelSnapDistance,
      );

      if (styled) {
        this.centerCurves = styled.curves;

        const origin = this.actionInProgress?.origin;
        const angles = this.lineAngles;
        // Where only lines of the assets cross, the height settings set its height, as for a
        // crossing away from the design. On a line of the design, it stays on that line.
        const point =
          styled.style === 'intersection' && !styled.intersection
            ? this.adjustSnapHeight(styled.point, rayCaster, basePoint)
            : styled.point;

        if (!point) {
          return undefined;
        }

        if (!isDefined(angles) || !origin) {
          this.intersection = styled.intersection;
          return point;
        }

        // Snapped to like a point of the design, where it is on the line at the angle.
        const snapPoint = this.snapToLineAngle(point, angles);
        const snapped = snapToTrajectory(
          origin,
          snapPoint.clone().sub(origin),
          { type: 'point', position: point },
          (p) => camera.getFrustum().containsPoint(p),
        );

        this.intersection = snapped?.attached ? styled.intersection : undefined;
        this.crossedLine = snapped?.crossedLine;
        return snapped?.point;
      }

      if (closestIntersection && this.freeSnapping) {
        // Handle angled edit-intersection (when hovering over a point or line on the geometry with angle snapping on)
        const angles = this.lineAngles;

        if (isDefined(angles) && this.actionInProgress?.origin) {
          const result = { snapPoint: this.snapToLineAngle(closestIntersection.point, angles) };

          {
            const position = closestIntersection.point;
            let target: TrajectorySnapTarget = { type: 'face', position };

            if (closestIntersection.object instanceof Points) {
              target = { type: 'point', position };
            } else if (isLineSegmentObject(closestIntersection.object)) {
              const line = this.asset.designNode.getLineSegmentByIndex(closestIntersection.index!);
              const [start, end] = this.asset.designNode.getVerticesByLineSegmentId(
                line.id,
                'world',
              );
              const conic = getLineSegmentConic(this.asset.designNode, line.id, 'world');
              target = conic
                ? { type: 'curve', position, conic }
                : { type: 'line', position, start, end };
            }

            const origin = this.actionInProgress.origin;
            const snapped = snapToTrajectory(
              origin,
              result.snapPoint.clone().sub(origin),
              target,
              (p) => camera.getFrustum().containsPoint(p),
            );

            this.intersection = snapped?.attached ? closestIntersection : undefined;
            this.crossedLine = snapped?.crossedLine;
            return snapped?.point;
          }
        } else {
          this.intersection = closestIntersection;
        }

        return closestIntersection.point;
      }
    }

    this.intersection = undefined;

    return getSafeVector3(this.findFlexibleSnapPoint(camera, rayCaster, basePoint));
  }

  /**
   * The angles (radians) from the reference direction that lines are snapped to,
   * or undefined if lines are not snapped to an angle.
   * Holding shift always snaps lines perpendicular.
   */
  private get lineAngles(): number[] | undefined {
    if (this.inputHandler.shiftIsDown) {
      return perpendicularAngles;
    }

    switch (this.snapSettings.angle) {
      case 'perpendicular':
        return perpendicularAngles;
      case 'custom': {
        // A custom angle only snaps to that angle from the reference line, not to its multiples,
        // since the line would otherwise jump between many directions with small angles.
        // Check the converted angle, since multiples of 360 degrees are converted to 0.
        const angle = degreesToRadians(this.snapValues.angle);
        return angle > 0 ? getAnglesToLine(angle) : undefined;
      }
      default:
        return undefined;
    }
  }

  /**
   * Gets the line that the angle of a new line is relative to.
   * This is the line the new line starts on, or any line connected to the point it starts on.
   * If there is no such line, the angle is relative to east.
   * If the line is curved, the angle is relative to the direction of the curve where the new line starts.
   */
  private getLineReference(): LineReference {
    let referenceLine = isDefined(this.actionInProgress?.originLineSegmentId)
      ? this.asset.designNode.getLineSegmentById(this.actionInProgress.originLineSegmentId)
      : undefined;

    // If the reference line is not set and we have an origin point,
    // then use any line segment connected to that point as the reference line.
    if (!isDefined(referenceLine) && isDefined(this.actionInProgress?.originPointId)) {
      const result = this.asset.designNode.getPointById(this.actionInProgress.originPointId);

      if (result.segments.length > 0) {
        referenceLine = this.asset.designNode.getLineSegmentById(result.segments[0]);
      }
    }

    if (!referenceLine) {
      const { originAssetLine, origin } = this.actionInProgress ?? {};

      return originAssetLine && origin
        ? getAssetLineReference(originAssetLine, origin)
        : { direction: new Vector2(1, 0) };
    }

    const [start, end] = this.asset.designNode.getVerticesByLineSegmentId(
      referenceLine.id,
      'world',
    );
    const origin = this.actionInProgress?.origin;
    const conic = getLineSegmentConic(this.asset.designNode, referenceLine.id, 'world');

    if (conic && origin) {
      const direction = getConicDirection(conic, getConicParameter(conic, origin));
      const tangent = new Vector3(direction.x, direction.y, 0);

      return {
        direction,
        line: { start: origin.clone().sub(tangent), end: origin.clone().add(tangent) },
      };
    }

    return {
      direction: new Vector2(end.x - start.x, end.y - start.y).normalize(),
      line: { start, end },
    };
  }

  /**
   * Snaps the direction from the origin of the line to the point to the closest of the angles,
   * keeping the height of the point.
   */
  /**
   * Gets an intersection with a point of the design, as when hovering it.
   */
  private getPointIntersection(id: PointId): Intersection | undefined {
    const node = this.asset.designNode;
    const index = node.findIndexByPointId(id);

    return isDefined(index)
      ? {
          object: node.data.points,
          index,
          point: node.getVertexByPointId(id, 'world'),
          distance: 0,
        }
      : undefined;
  }

  /**
   * Gets an intersection with a line segment of the design at the point, as when hovering it.
   */
  private getLineSegmentIntersection(id: LineSegmentId, point: Vector3): Intersection | undefined {
    const node = this.asset.designNode;
    const index = node.findIndexByLineSegmentId(id);

    return isDefined(index)
      ? { object: node.data.styledLines, index, point: point.clone(), distance: 0 }
      : undefined;
  }

  /**
   * Gets where two lines near the cursor cross, for the intersection snap style: lines of the
   * design when includeDesign, and of the assets that are snapped to. A crossing on a line of the
   * design is on that line segment, as when hovering it.
   */
  private getCrossingTarget(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    includeDesign: boolean,
  ): { point: Vector3; intersection?: Intersection } | undefined {
    type Line = CrossingLine & { pixels: number; key: string; lineSegmentId?: LineSegmentId };
    const node = this.asset.designNode;
    const cursor = getRayCursor(camera, rayCaster);
    const lines = new Map<string, Line>();

    const addLine = (line: Line) => {
      if (line.pixels <= this.pixelSnapDistance && !lines.has(line.key)) {
        lines.set(line.key, line);
      }
    };

    if (includeDesign) {
      const objects = [node.data.lineSegments, node.data.styledLines];

      for (const intersection of rayCaster.intersectObjects(objects, true)) {
        const id = findLineSegmentIdFromIntersection(node, intersection);

        if (isDefined(id)) {
          const [start, end] = node.getVerticesByLineSegmentId(id, 'world');
          addLine({
            start,
            end,
            conic: getLineSegmentConic(node, id, 'world'),
            pixels: camera.getScreenPosition(intersection.point).distanceTo(cursor),
            key: `design:${id}`,
            lineSegmentId: id,
          });
        }
      }
    }

    if (this.snapValues.assets) {
      getAssetCrossingLines(camera, rayCaster, this.isSnapAsset, this.pixelSnapDistance).forEach(
        addLine,
      );
    }

    // The lines closest to the cursor, since every pair of them is crossed.
    const closest = [...lines.values()]
      .sort((a, b) => a.pixels - b.pixels)
      .slice(0, maxCrossingLines);
    const crossing = findClosestCrossing(
      closest,
      (p) => camera.getScreenPosition(p),
      cursor,
      this.pixelSnapDistance,
    );

    if (!crossing) {
      return undefined;
    }

    const [first, second] = crossing.lines.filter((x) => isDefined(x.lineSegmentId));

    if (!isDefined(first?.lineSegmentId)) {
      return { point: crossing.point };
    }

    // On the line of the design, at its height there.
    const point = crossing.point.clone().setZ(getHeightOnLine(first, crossing.point));
    const tolerance = 1e-9 * (1 + point.length());

    // Where lines of the design meet at a point, e.g. the corner of a polyline, it is that point.
    const pointId = [first, second]
      .flatMap((x) =>
        isDefined(x?.lineSegmentId) ? node.getLineSegmentById(x.lineSegmentId).values : [],
      )
      .find((x) => node.getVertexByPointId(x, 'world').distanceTo(point) <= tolerance);
    const pointIntersection = isDefined(pointId) ? this.getPointIntersection(pointId) : undefined;

    if (pointIntersection) {
      // Where one line of the design ends on the other (a T), a point placed there breaks the
      // other line, if it is at the same height.
      const through = [first, second].find(
        (x) =>
          isDefined(x?.lineSegmentId) &&
          !node.getLineSegmentById(x.lineSegmentId).values.includes(pointId!),
      );
      const crossedLineSegmentId =
        through &&
        Math.abs(getHeightOnLine(through, pointIntersection.point) - pointIntersection.point.z) <=
          tolerance
          ? through.lineSegmentId
          : undefined;

      return {
        point: pointIntersection.point,
        intersection: { ...pointIntersection, crossedLineSegmentId } as CrossingIntersection,
      };
    }

    const intersection = this.getLineSegmentIntersection(first.lineSegmentId, point);
    // A point placed there breaks both lines, if they cross at the same height.
    const crossedLineSegmentId =
      second && Math.abs(getHeightOnLine(second, point) - point.z) <= tolerance
        ? second.lineSegmentId
        : undefined;

    return {
      point,
      intersection:
        intersection && ({ ...intersection, crossedLineSegmentId } as CrossingIntersection),
    };
  }

  /**
   * Gets the points of the design that the snap styles target near the cursor: of the hovered
   * geometry, the centers of curves near the cursor, and where lines cross, also lines of the
   * assets when the design is hovered. What a point is on is its intersection, see
   * getSnapStyleTarget.
   */
  private getDesignSnapCandidates(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    intersections: Intersection[],
  ): DesignSnapCandidate[] {
    const cursor = getRayCursor(camera, rayCaster);
    const toCandidates = (
      style: SnapStyle,
      target: { point: Vector3; intersection?: Intersection } | undefined,
    ): DesignSnapCandidate[] =>
      target
        ? [{ style, ...target, pixels: camera.getScreenPosition(target.point).distanceTo(cursor) }]
        : [];

    return this.snapStyles.flatMap((style) => {
      if (style === 'intersection') {
        return intersections.length > 0
          ? toCandidates(style, this.getCrossingTarget(camera, rayCaster, true))
          : [];
      }

      // The centers of curves first, which are highlighted, as the hovered curve is among them.
      return [
        ...(style === 'center' ? this.getDesignCurveCenters(camera, cursor, intersections) : []),
        ...intersections.flatMap((x) => toCandidates(style, this.getSnapStyleTarget(x, style))),
      ];
    });
  }

  /**
   * Gets the centers of the curves of the design within the snap distance of the cursor, and of the
   * hovered curves at any distance, with the curves to highlight: a whole circle for a circle.
   */
  private getDesignCurveCenters(
    camera: SkyviewCamera,
    cursor: Vector2,
    intersections: Intersection[],
  ): DesignSnapCandidate[] {
    const node = this.asset.designNode;
    const hovered = new Set(
      intersections.map((x) => findLineSegmentIdFromIntersection(node, x)).filter(isDefined),
    );
    const visited = new Set<LineSegmentId>();
    const candidates: DesignSnapCandidate[] = [];

    for (const id of node.lineSegments) {
      if (visited.has(id) || !isDefined(node.getLineSegmentById(id).curve)) {
        continue;
      }

      const ids = withWholeCircles(node, [id]);
      ids.forEach((x) => visited.add(x));

      const intersection = this.getLineSegmentIntersection(
        id,
        node.getVerticesByLineSegmentId(id, 'world')[0],
      );
      const target = intersection && this.getSnapStyleTarget(intersection, 'center');

      if (!target) {
        continue;
      }

      const pixels = camera.getScreenPosition(target.point).distanceTo(cursor);
      const near =
        pixels <= this.pixelSnapDistance && camera.getFrustum().containsPoint(target.point);

      if (near || ids.some((x) => hovered.has(x))) {
        const curves = ids.map((x) => getLineSegmentConic(node, x, 'world')).filter(isDefined);
        candidates.push({ style: 'center', ...target, pixels, curves });
      }
    }

    return candidates;
  }

  /**
   * Gets the point of the hovered geometry of the design that the snap style targets, and what it
   * is on: the point at a corner, the line segment for its middle, and nothing for the center of a
   * curve. Points are snapped to as they are.
   */
  private getSnapStyleTarget(
    intersection: Intersection,
    style: SnapStyle,
  ): { point: Vector3; intersection?: Intersection } | undefined {
    const node = this.asset.designNode;
    const pointId = findPointIdFromIntersection(node, intersection);
    const lineSegmentId = findLineSegmentIdFromIntersection(node, intersection);
    const faceId = findFaceIdFromIntersection(node, intersection);

    // A point is a corner, and the center point of a circle is its center.
    if (isDefined(pointId)) {
      const isCenter = getCenterCircles(node, pointId).length > 0;
      return style === 'corner' || (style === 'center' && isCenter)
        ? { point: intersection.point, intersection }
        : undefined;
    }

    if (isDefined(lineSegmentId)) {
      const [start, end] = node.getVerticesByLineSegmentId(lineSegmentId, 'world');
      const conic = getLineSegmentConic(node, lineSegmentId, 'world');
      // The curves it continues into, which decide whether an arc of an ellipse has a center.
      const connected = node
        .getLineSegmentById(lineSegmentId)
        .values.flatMap((id) => node.getPointById(id).segments)
        .filter((id) => id !== lineSegmentId)
        .map((id) => getLineSegmentConic(node, id, 'world'))
        .filter(isDefined);
      const point = getLineSnapStylePoint(style, intersection.point, start, end, conic, connected);

      if (!point) {
        return undefined;
      }

      switch (style) {
        case 'corner': {
          const [a, b] = node.getLineSegmentById(lineSegmentId).values;
          return { point, intersection: this.getPointIntersection(point.equals(start) ? a : b) };
        }
        case 'midpoint':
          return { point, intersection: { ...intersection, point } };
        case 'center': {
          // The center of a circle is its center point.
          const circle = getLineSegmentCircle(node, lineSegmentId);
          const centerIntersection = circle && this.getPointIntersection(circle.center);
          return centerIntersection
            ? { point: centerIntersection.point, intersection: centerIntersection }
            : { point };
        }
      }
    }

    if (isDefined(faceId) && style === 'corner') {
      const [id] = [...node.getFaceById(faceId).values].sort(
        (a, b) =>
          node.getVertexByPointId(a, 'world').distanceTo(intersection.point) -
          node.getVertexByPointId(b, 'world').distanceTo(intersection.point),
      );
      const intersectionWithPoint = this.getPointIntersection(id);
      return (
        intersectionWithPoint && {
          point: intersectionWithPoint.point,
          intersection: intersectionWithPoint,
        }
      );
    }

    return undefined;
  }

  private snapToLineAngle(point: Vector3, angles: number[]): Vector3 {
    const origin = this.actionInProgress!.origin!;
    const direction = snapDirectionToAngle(
      new Vector2(point.x - origin.x, point.y - origin.y),
      this.getLineReference().direction,
      angles,
    );

    return new Vector3(origin.x + direction.x, origin.y + direction.y, point.z);
  }

  /**
   * Gets the point at the height under the cursor, or above or below the base point.
   */
  private getPointOnHeight(
    height: number,
    rayCaster: Raycaster,
    basePoint?: Vector3,
  ): Vector3 | undefined {
    return (
      basePoint?.clone().setZ(height) ??
      findPointOnLineFromHeight(rayCaster.ray.origin, rayCaster.ray.direction, height)
    );
  }

  /**
   * The previous point of the line being created, which the next point is placed from.
   */
  private get lineOrigin(): Vector3 | undefined {
    const action = this.actionInProgress;
    return action?.actionName === ActionName.CreateLine ||
      action?.actionName === ActionName.CreateCurve
      ? action.origin
      : undefined;
  }

  /**
   * Adjusts the height of a point on the snap targets by the height settings. The absolute height
   * comes before the height of what was snapped to, e.g. of where lines cross.
   */
  private adjustSnapHeight(
    target: Vector3 | undefined,
    rayCaster: Raycaster,
    basePoint?: Vector3,
  ): Vector3 | undefined {
    switch (this.snapSettings.height) {
      case 'absolute': {
        // A base point keeps its position and only gets a new height, e.g. when resnapping a point.
        const height = this.snapValues.height - CoordinateManager.getInstance().getMAS();
        return (
          (basePoint ?? target)?.clone().setZ(height) ??
          this.getPointOnHeight(height, rayCaster, basePoint)
        );
      }
      case 'offset': {
        // From the second point of a line, the z-offset is from the previous point, as for an
        // offset from the line it offsets. Otherwise from the surface that is snapped to.
        const origin = this.lineOrigin;

        if (!origin) {
          return target?.clone().setZ(target.z + this.snapValues.heightOffset);
        }

        const height = origin.z + this.snapValues.heightOffset;
        return target?.clone().setZ(height) ?? this.getPointOnHeight(height, rayCaster, basePoint);
      }
      default:
        return target;
    }
  }

  /**
   * Finds the snap point when flexible snapping is enabled.
   *
   * 1. The cursor snaps towards the selected snap targets, then its height is adjusted.
   * 2. If a line is being created, the line constraints (angle, length and incline) are applied.
   */
  private findFlexibleSnapPoint(
    camera: SkyviewCamera,
    rayCaster: Raycaster,
    basePoint?: Vector3,
  ): Vector3 | undefined {
    const origin = this.actionInProgress?.origin;
    const pointOnHeight = (height: number) => this.getPointOnHeight(height, rayCaster, basePoint);

    if (this.actionInProgress?.actionName === ActionName.CreateText) {
      return pointOnHeight(origin!.z);
    }

    const { pointCloud, tiledModel, assets } = this.snapValues;
    // The assets are intersected first, so that it is known which line of an asset the cursor is on.
    // The snap styles and a line snapped to an angle target lines, so the outline of a filled shape
    // is preferred over its fill, like the lines of the design.
    const styles = this.snapStyles;
    const targetsLines = styles.length > 0 || (isDefined(origin) && isDefined(this.lineAngles));
    const assetIntersection = assets
      ? Pick.instance.closestAssetIntersection(
          camera,
          rayCaster,
          this.isSnapAsset,
          targetsLines ? this.pixelSnapDistance : undefined,
        )
      : undefined;

    // The point near the cursor that the snap styles target, e.g. the end of the hovered line.
    const styled = assets
      ? chooseSnapCandidate(
          findAssetSnapCandidates(
            camera,
            rayCaster,
            styles,
            assetIntersection,
            this.isSnapAsset,
            this.pixelSnapDistance,
          ),
          this.freeSnapping,
          this.pixelSnapDistance,
        )
      : undefined;
    const stylePoint = styled?.point;
    this.centerCurves = styled?.curves;

    // The hovered position on the asset, point cloud or tiled model, without a snap style.
    const freePoint = assetIntersection
      ? Pick.instance.getIntersectionPoint(assetIntersection)
      : pointCloud || tiledModel
        ? Pick.instance.closestPoint(camera, rayCaster, {
            skipAssets: true,
            skipPointCloud: !pointCloud,
            skipTiledModel: !tiledModel,
          })
        : undefined;

    // Without free snapping, only the points of the snap styles are snapped to, see below.
    const styleMissed = !this.freeSnapping && !isDefined(stylePoint);
    this.assetLine = styleMissed ? undefined : getAssetLine(assetIntersection);
    const surfacePoint = stylePoint ?? freePoint;

    // An incline sets the height of the next point of a line instead of the height settings, which
    // then don't decide where the cursor is either: at the previous point's height, see below, or
    // on what it snaps to. Cut/fill extends the line to the height all the same.
    const inclined = isDefined(origin) && isDefined(this.snapSettings.incline);
    let point = inclined
      ? surfacePoint?.clone()
      : this.adjustSnapHeight(surfacePoint, rayCaster, basePoint);

    // Without free snapping, there is no point anywhere but at those points: neither on the hovered
    // geometry nor on the point cloud or at a height. The hidden cursor follows the hovered
    // position all the same, since how far geometry is picked from depends on its distance.
    if (styleMissed) {
      const hovered = point ?? (isDefined(origin) ? pointOnHeight(origin.z) : undefined);

      if (hovered) {
        this.cursorPoint.position.copy(hovered);
      }

      return undefined;
    }

    if (!isDefined(origin)) {
      return point;
    }

    // When there is nothing to snap towards, the line continues at the same height.
    point ??= pointOnHeight(origin.z);

    if (!point) {
      return;
    }

    const lengthMode = this.snapSettings.length;
    const constraints: LineConstraints = {
      referenceDirection: this.getLineReference().direction,
      angles: this.lineAngles,
      length:
        lengthMode === '2d' || lengthMode === '3d'
          ? { value: this.snapValues.length, mode: lengthMode }
          : undefined,
      // Percent, degrees and ratio are all stored as a slope in percent.
      slopePercent: isDefined(this.snapSettings.incline) ? this.snapValues.slope : undefined,
    };

    point = applyLineConstraints(origin, point, constraints);

    // A line snapped to an angle ends where it crosses the line of an asset under the cursor, also
    // where it crosses its extension, or passes the point of the snap style, like for the design,
    // unless its length is set.
    if (
      isDefined(point) &&
      isDefined(constraints.angles) &&
      !constraints.length &&
      (stylePoint || this.assetLine)
    ) {
      const snapped = snapToTrajectory(
        origin,
        point.clone().sub(origin),
        stylePoint
          ? { type: 'point', position: stylePoint }
          : getAssetLineTarget(this.assetLine!, surfacePoint ?? point),
        (p) => camera.getFrustum().containsPoint(p),
      );

      // The point of the snap style is projected onto the line at the angle like a point of the
      // design, also when the line doesn't pass it.
      if (snapped?.attached || snapped?.crossedLine || (stylePoint && snapped)) {
        // The line of the asset is extended in its own color.
        this.crossedLine = snapped.crossedLine && {
          ...snapped.crossedLine,
          color: getIntersectedLineColor(assetIntersection),
        };
        return applyLineConstraints(
          origin,
          this.adjustSnapHeight(snapped.point, rayCaster, basePoint)!,
          constraints,
        );
      }
    }

    if (lengthMode === 'cutFill' && isDefined(point)) {
      return this.intersectSnapTargets(origin, point) ?? point;
    }

    return point;
  }

  /**
   * The height that a line or an offset from the origin is extended to by cut/fill, like the snap
   * targets: the absolute height, or the z-offset from the origin, e.g. from the polygon that is
   * offset.
   */
  private getCutFillHeight(origin: Vector3): number | undefined {
    switch (this.snapSettings.height) {
      case 'absolute':
        return this.snapValues.height - CoordinateManager.getInstance().getMAS();
      case 'offset':
        return origin.z + this.snapValues.heightOffset;
      default:
        return undefined;
    }
  }

  /**
   * Extends the line from the origin through the point until it hits any of the snap targets,
   * and returns the closest hit. The height settings set a height that is one of them, see
   * getCutFillHeight, e.g. for a slope from the ground up to it.
   * @param pointCloudChunks Point cloud chunks to intersect instead of the loaded point cloud.
   */
  intersectSnapTargets(
    origin: Vector3,
    point: Vector3,
    pointCloudChunks?: Points[],
  ): Vector3 | undefined {
    const { tiledModel, assets } = this.snapValues;
    const targets = (
      [
        [this.snapsToPointCloud, 'pointcloud'],
        [tiledModel, 'tiledModel'],
        [assets, 'asset'],
      ] as const
    )
      .filter(([enabled]) => enabled)
      .map(([, target]) => target);

    const hits = targets.map((target) =>
      Pick.instance.onRay(this.asset, origin, point, target, pointCloudChunks, this.isSnapAsset),
    );
    const height = this.getCutFillHeight(origin);

    if (isDefined(height)) {
      hits.push(extendToHeight(origin, point, height));
    }

    let closestHit: Vector3 | undefined;

    for (const hit of hits) {
      if (
        isValidVector3(hit) &&
        (!closestHit || hit!.distanceTo(origin) < closestHit.distanceTo(origin))
      ) {
        closestHit = hit;
      }
    }

    return closestHit;
  }

  placePointer(e: MouseInputData) {
    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return;
    }

    this.currentPoint = this.findSnapPoint(e, camera);
    this.updateCurveHandle(e, camera);
    this.updateCircleHandles(e, camera);
    // After the handles, which can take the cursor.
    this.centerHighlight.show(this.currentPoint && this.centerCurves);
    this.heightsGuide.update(camera, this.currentPoint);
    this.updateAngleGuide();
    this.updateInclineGuide();
    // There is no point where nothing can be snapped to, e.g. away from the points of a snap style.
    this.cursorPoint.visible = isDefined(this.currentPoint);

    if (!this.currentPoint) {
      this.cursorLine.visible = false;
      this.cursorFace.visible = false;

      // Shown after the action is refreshed, see onMouseMove.
      if (!this.actionInProgress?.tooltipPoint) {
        this.hideTooltip();
      }

      return true;
    }

    // eslint-disable-next-line no-prototype-builtins
    if (!e.hasOwnProperty('isNativeFunction')) {
      this.updateTooltip({ x: e.pointer.client.x, y: e.pointer.client.y });
    }

    this.cursorPoint.position.set(this.currentPoint.x, this.currentPoint.y, this.currentPoint.z);

    const { isLineHighlighted, isFaceHighlighted } = this.getSelectionType();
    this.cursorLine.visible = isLineHighlighted;
    this.cursorFace.visible = isFaceHighlighted;

    if (this.intersection) {
      this.cursorPoint.setColor(snapColor);

      if (this.cursorLine.visible) {
        const line = this.asset.designNode.getLineSegmentByIndex(this.intersection?.index!);
        // A circle is highlighted as one.
        this.cursorLine.setVertices(
          withWholeCircles(this.asset.designNode, [line.id]).flatMap((id) =>
            getLineSegmentCursorVertices(this.asset.designNode, id),
          ),
        );
      } else if (this.cursorFace.visible) {
        const face = this.asset.designNode.getFaceByIndex(this.intersection.faceIndex!);
        this.cursorFace.setNonIndexedVertices(
          this.asset.designNode.getVerticesByFaceId(face.id, 'world'),
        );
      }
    } else if (this.actionInProgress) {
      this.cursorPoint.setColor(new Color(0, 1, 0));
    } else {
      this.cursorPoint.setColor(new Color(defaultCursorColor));
    }
  }

  /**
   * Shows a handle in the middle of the hovered line segment in curve mode, and snaps the cursor
   * to it when it is close. Pressing M on the handle bends the line segment, see onKeyDown.
   */
  private updateCurveHandle(e: MouseInputData, camera: SkyviewCamera) {
    const node = this.asset.designNode;
    const lineSegmentId =
      this.designMode === DesignMode.Curve && !this.actionInProgress
        ? findLineSegmentIdFromIntersection(node, this.intersection)
        : undefined;

    // The arcs of circles have handles of their own, see updateCircleHandles.
    this.hoveredCurveHandle = undefined;
    this.curveHandle.visible =
      isDefined(lineSegmentId) &&
      getLineSegmentFaces(node, lineSegmentId).length === 0 &&
      !isDefined(getLineSegmentCircle(node, lineSegmentId));

    if (!this.curveHandle.visible) {
      return;
    }

    const middle = getLineSegmentMiddle(node, lineSegmentId!);
    const onHandle =
      camera.getScreenPosition(middle).distanceTo(e.pointer.client) < this.pixelSnapDistance;

    this.curveHandle.position.copy(middle);
    this.curveHandle.setColor(onHandle ? snapColor : new Color(curveHandleColor));

    if (onHandle && this.intersection) {
      // Snap to the middle of the line segment, creating from here splits it in the middle.
      this.hoveredCurveHandle = lineSegmentId;
      this.currentPoint = middle.clone();
      this.centerCurves = undefined;
      this.intersection = { ...this.intersection, point: middle.clone() };
    }
  }

  private hideCircleHandles() {
    [...this.circleHandles.corners, ...this.circleHandles.middles].forEach(
      (x) => (x.visible = false),
    );
    this.hoveredCircleHandle = undefined;
  }

  /**
   * Shows the handles of the hovered circle or ellipse, and snaps the cursor to the one it is close
   * to: to the point at the end of an axis, or to the middle of an arc, on the arc.
   */
  private updateCircleHandles(e: MouseInputData, camera: SkyviewCamera) {
    const node = this.asset.designNode;
    // An arc of the circle, or a seam when the cursor is snapped to a corner.
    const lineSegmentId = findLineSegmentIdFromIntersection(node, this.intersection);
    const pointId = findPointIdFromIntersection(node, this.intersection);
    const circle = this.actionInProgress
      ? undefined
      : isDefined(lineSegmentId)
        ? getLineSegmentCircle(node, lineSegmentId)
        : isDefined(pointId)
          ? getSeamCircle(node, pointId)
          : undefined;

    this.hideCircleHandles();

    if (!circle || circle.seams.length !== 4) {
      return;
    }

    const positions = getCircleHandles(node, circle);
    const handles = [
      ...positions.corners.map((position, index) => ({ kind: 'corner' as const, index, position })),
      ...positions.middles.map((position, index) => ({ kind: 'middle' as const, index, position })),
    ];
    const distanceOnScreen = (position: Vector3) =>
      camera.getScreenPosition(position).distanceTo(e.pointer.client);
    const closest = handles.reduce((a, b) =>
      distanceOnScreen(b.position) < distanceOnScreen(a.position) ? b : a,
    );
    const onHandle = distanceOnScreen(closest.position) < this.pixelSnapDistance;

    handles.forEach(({ kind, index, position }) => {
      const sphere = (kind === 'corner' ? this.circleHandles.corners : this.circleHandles.middles)[
        index
      ];
      sphere.visible = true;
      sphere.position.copy(position);
      sphere.setColor(
        onHandle && closest.kind === kind && closest.index === index
          ? snapColor
          : new Color(curveHandleColor),
      );
    });

    const intersection =
      closest.kind === 'corner'
        ? this.getPointIntersection(circle.seams[closest.index])
        : this.getLineSegmentIntersection(circle.lineSegments[closest.index], closest.position);

    if (onHandle && intersection) {
      this.hoveredCircleHandle = { circle, kind: closest.kind, index: closest.index };
      this.currentPoint = closest.position.clone();
      this.centerCurves = undefined;
      this.intersection = intersection;
    }
  }

  /**
   * Shows the angle guide if the current point is snapped to an angle from the origin of the action.
   */
  private updateAngleGuide() {
    const origin = this.actionInProgress?.origin;
    const angles = this.lineAngles;

    const guide =
      origin && this.currentPoint && isDefined(angles)
        ? getAngleGuide(origin, this.currentPoint, this.getLineReference(), angles)
        : undefined;

    if (guide) {
      this.angleGuide.show(guide, this.crossedLine);
    } else {
      this.angleGuide.hide();
    }
  }

  /**
   * Shows which way is uphill on the line being created, or on the hovered line
   * if no line is being created.
   */
  private updateInclineGuide() {
    const origin = this.actionInProgress?.origin;
    let line: [Vector3, Vector3] | undefined;
    let conic: Conic | undefined;

    if (this.actionInProgress?.actionName === ActionName.CreateLine && origin) {
      line = this.currentPoint && [origin, this.currentPoint];
    } else if (isLineSegmentObject(this.intersection?.object)) {
      const segment = this.asset.designNode.getLineSegmentByIndex(this.intersection!.index!);
      line = this.asset.designNode.getVerticesByLineSegmentId(segment.id, 'world');
      conic = getLineSegmentConic(this.asset.designNode, segment.id, 'world');
    }

    const slope = line ? vectorToSlopePercent(line[1].clone().sub(line[0])) : 0;

    // The slope is rounded like in the tooltip, so the arrow is only shown when it shows a slope.
    if (line && roundToDecimals(slope, 2) !== 0) {
      // The arrow is moved away from the curve handle in the middle of the line, when it is shown.
      const clearancePx = this.curveHandle.visible ? curveHandleSizePx / 2 : 0;
      this.inclineGuide.show(...line, conic, clearancePx);
    } else {
      this.inclineGuide.hide();
    }
  }

  lastMouseEvent: MouseInputData = {
    button: {
      type: MouseButtonType.LeftButton,
    },
    type: MouseEventType.MouseMove,
    pointer: {
      client: new Vector2(),
      delta: new Vector2(),
      position: new Vector2(),
    },
    wheel: { delta: 0 },
  };

  onMouseMove(e: MouseInputData) {
    this.lastMouseEvent = e;
    this.mouseInView = true;
    if (this.selectionBox.visible) {
      this.selectionEndPoint = e.pointer.position.clone();
      this.refreshSelectionBox();
      return false;
    }

    this.placePointer(e);

    if (this.actionInProgress && !this.actionInProgress.refreshInProgress) {
      // Prevent refresh from running many times simultaneously since some operations can be heavy
      this.actionInProgress.refreshInProgress = true;
      this.actionInProgress.refresh?.();
      this.actionInProgress.refreshInProgress = false;
    }

    // Where nothing is snapped to, the tooltip is about the action, which has just been refreshed.
    // eslint-disable-next-line no-prototype-builtins
    if (!this.currentPoint && !e.hasOwnProperty('isNativeFunction')) {
      if (this.actionInProgress?.tooltipPoint) {
        this.updateTooltip({ x: e.pointer.client.x, y: e.pointer.client.y });
      }
    }

    return true;
  }

  refreshSelectionBox() {
    const rayCast = new Raycaster();

    const getPoint = (pos: Vector2) => {
      rayCast.setFromCamera(pos, cameraManager().getActiveCamera()!.getTHREECamera());
      return rayCast.ray.origin.clone().add(rayCast.ray.direction);
    };

    this.selectionBox.setIndicesAndVertices(
      new Float32BufferAttribute(
        [
          ...getPoint(this.selectionStartPoint).toArray(),
          ...getPoint(this.selectionStartPoint.clone().setX(this.selectionEndPoint.x)).toArray(),
          ...getPoint(this.selectionStartPoint.clone().setY(this.selectionEndPoint.y)).toArray(),
          ...getPoint(this.selectionEndPoint).toArray(),
        ],
        3,
      ),
      [0, 2, 1, 1, 2, 3],
    );
  }

  onMouseOut(e: MouseInputData) {
    this.mouseInView = false;
    this.clearTooltip();
    return true;
  }

  onMouseDown(e: MouseInputData) {
    this.contextMenuCallback?.();
    this.mousePosition = e.pointer.client;

    const startMultiSelect =
      e.button.type === MouseButtonType.LeftButton &&
      (this._activeTool === DesignMode.Select ||
        this.inputHandler.ctrlIsDown ||
        this.inputHandler.shiftIsDown);

    if (!this.actionInProgress && startMultiSelect) {
      this.selectionStartPoint = e.pointer.position.clone();
      this.selectionEndPoint = this.selectionStartPoint.clone();
      this.refreshSelectionBox();
      this.selectionBox.visible = true;
      return false;
    }

    // Start drawing picker line in text mode.
    if (
      !this.actionInProgress &&
      this._activeTool === DesignMode.Text &&
      e.button.type === MouseButtonType.LeftButton &&
      this.currentPoint
    ) {
      void commands.createTextAction(this).action();
      return false;
    }

    return true;
  }

  onDoubleClick(e: MouseInputData) {
    if (this.intersection) {
      this.selectionBox.visible = false;
      if (this.actionInProgress) {
        // Disable multiselect during certain actions.
        if (
          [ActionName.InterpolatePointHeights, ActionName.SelectPolyLine].includes(
            this.actionInProgress.actionName,
          )
        ) {
          return false;
        }

        void commands.abortActionInProgress(this).action();
      }

      void quickSelectLineOrPolygon(this);
    }

    return true;
  }

  async onMouseUp(e: MouseInputData) {
    if (this.selectionBox.visible && e.button.type === MouseButtonType.LeftButton) {
      this.handleSelect(e);
      return true;
    }

    const allowMouseMovement =
      e.button.type === MouseButtonType.LeftButton && this._activeTool === DesignMode.Text;

    if (this.mouseMoved(e) && !allowMouseMovement) {
      return true;
    }

    if (e.button.type === MouseButtonType.RightButton) {
      this.updateContextMenu(e);
      return true;
    }

    if (e.button.type === MouseButtonType.LeftButton) {
      if (this.actionInProgress) {
        // If action in progress has custom mouseUp behaviour do that instead.
        if (isDefined(this.actionInProgress.onMouseUp)) {
          this.actionInProgress.onMouseUp(e);
          return true;
        }

        if (!this.actionInProgress.readyToApply()) {
          return true;
        }

        const creatingLine = this.actionInProgress.actionName === ActionName.CreateLine;

        // Holding Alt, the line is a curve instead, which the next click bends
        if (creatingLine && this.inputHandler.altIsDown && this.designCurves) {
          commands.bendLineInProgress(this);
          this.updateTooltip();
          return true;
        }
        await commands.applyActionInProgress(this).action(e);

        // If last command was create line, then start creating a new line automatically
        if (creatingLine) {
          await commands.createLine(this).action();
          // Emulate mouse moved so the visuals are updated.
          this.onMouseMove(e);
        }
      } else if (this.currentPoint && !this.multiSelectInProgress) {
        // Geometry is created at a handle of a circle once it is arcs and points like any other.
        if (this.hoveredCircleHandle) {
          const { circle, kind, index } = this.hoveredCircleHandle;
          const intersection = this.intersection;
          breakCircle(this, circle);
          this.hideCircleHandles();
          // The seam is drawn now, and is at another index.
          this.intersection =
            kind === 'corner' ? this.getPointIntersection(circle.seams[index]) : intersection;
        }

        if (this.inputHandler.altIsDown || this._activeTool === DesignMode.Point) {
          await commands.createPoint(this).action();
        } else if (this._activeTool === DesignMode.Curve) {
          await commands.createCurve(this).action();

          // Emulate mouse moved so the visuals are updated.
          this.onMouseMove(e);
        } else if (this._activeTool === DesignMode.Circle) {
          await commands.createCircle(this).action();
          this.onMouseMove(e);
        } else {
          await commands.createLine(this).action();
        }
      }
    }

    this.updateTooltip();

    return true;
  }

  /**
   * Handles multiselect. If the mouse has moved a considerable distance it will find all objects
   * contained within the selection box. otherwise it will perform a singular raycast on the
   * mouse position to pick the currently hovered point, line or face.
   */
  handleSelect(e: MouseInputData) {
    let newSelection: SelectionResult = { faces: [], points: [], lineSegments: [] };

    // Multi select
    if (!this.mousePosition || this.mouseMoved(e)) {
      const camera = cameraManager().getActiveCamera()!;
      const rayCast = new Raycaster();
      rayCast.setFromCamera(this.selectionStartPoint, camera.getTHREECamera());
      const screenPointA = camera.getScreenPosition(
        rayCast.ray.origin.clone().add(rayCast.ray.direction),
      );
      rayCast.setFromCamera(this.selectionEndPoint, camera.getTHREECamera());
      const screenPointB = camera.getScreenPosition(
        rayCast.ray.origin.clone().add(rayCast.ray.direction),
      );
      const boundingBox = new Box2(
        new Vector2(
          Math.min(screenPointA.x, screenPointB.x),
          Math.min(screenPointA.y, screenPointB.y),
        ),
        new Vector2(
          Math.max(screenPointA.x, screenPointB.x),
          Math.max(screenPointA.y, screenPointB.y),
        ),
      );
      const frustum = camera.getFrustum();
      const selectedPoints: PointId[] = [];
      // Check which points are included in the selection
      for (const id of this.asset.designNode.points) {
        const point = this.asset.designNode.getVertexByPointId(id, 'world');
        if (!frustum.containsPoint(point)) {
          continue;
        }
        const screenPoint = camera.getScreenPosition(point);
        if (boundingBox.containsPoint(screenPoint)) {
          selectedPoints.push(id);
        }
      }
      newSelection = getMultiselectResult(this, selectedPoints);
    } else if (this.intersection) {
      // Single object select
      const pointId = findPointIdFromIntersection(this.asset.designNode, this.intersection);
      const lineSegmentId = findLineSegmentIdFromIntersection(
        this.asset.designNode,
        this.intersection,
      );
      const faceId = findFaceIdFromIntersection(this.asset.designNode, this.intersection);

      if (isDefined(pointId)) {
        newSelection.points.push(pointId);
      } else if (isDefined(lineSegmentId)) {
        newSelection.lineSegments.push(lineSegmentId);
      } else if (isDefined(faceId)) {
        newSelection.faces.push(faceId);
      }
    }

    const selectionMode = InputHandler.instance.shiftIsDown ? 'deselect' : 'select';
    this.refreshSelectedPointsCursor(newSelection.points, selectionMode);
    this.refreshSelectedLineSegmentsCursor(newSelection.lineSegments, selectionMode);
    this.refreshSelectedFacesCursor(newSelection.faces, selectionMode);
    // If nothing is selected, abort selection with deselect.
    if (!this.multiSelectInProgress) {
      deselect(this);
    }
    publish(SubscriptionTopic.DesignToolMultiselectUpdated, undefined);
    this.selectionBox.visible = false;
    this.updateTooltip();
  }

  private filterIds<T extends PointId | LineSegmentId | FaceId>(
    array: T[],
    newSelection: T[],
    mode: 'select' | 'deselect',
  ): T[] {
    if (mode === 'deselect') {
      array = array.filter((x) => !newSelection.includes(x));
    } else {
      const filteredItems = newSelection.filter((x) => !array.includes(x));
      // Do not use spread operator since it will cause max call stack error
      // when the array is too large.
      array = array.concat(filteredItems);
    }
    return array;
  }

  refreshCurrentSelection() {
    this.refreshSelectedFacesCursor([]);
    this.refreshSelectedLineSegmentsCursor([]);
    this.refreshSelectedPointsCursor([]);
  }

  refreshSelectedPointsCursor(newSelection: PointId[], mode: 'select' | 'deselect' = 'select') {
    // The seams of circles are not selected on their own, see circle.ts.
    this.currentSelection.points = this.filterIds(
      this.currentSelection.points,
      newSelection.filter((id) => !isHiddenSeam(this.asset.designNode, id)),
      mode,
    );

    this.selectedPoints.position.set(0, 0, 0);
    this.selectedPoints.setNonIndexedVertices(
      this.currentSelection.points.map((x) => this.asset.designNode.getVertexByPointId(x, 'world')),
    );
    this.selectedPoints.visible = true;
  }

  refreshSelectedLineSegmentsCursor(
    newSelection: LineSegmentId[],
    mode: 'select' | 'deselect' = 'select',
  ) {
    // A circle is selected as one.
    this.currentSelection.lineSegments = this.filterIds(
      this.currentSelection.lineSegments,
      withWholeCircles(this.asset.designNode, newSelection),
      mode,
    );

    this.selectedLineSegments.position.set(0, 0, 0);
    this.selectedLineSegments.setNonIndexedVertices(
      this.currentSelection.lineSegments
        .map((x) => getLineSegmentCursorVertices(this.asset.designNode, x))
        .flat(),
    );
    this.selectedLineSegments.visible = true;
  }

  refreshSelectedFacesCursor(newSelection: FaceId[], mode: 'select' | 'deselect' = 'select') {
    this.currentSelection.faces = this.filterIds(this.currentSelection.faces, newSelection, mode);

    this.selectedFaces.position.set(0, 0, 0);
    this.selectedFaces.setNonIndexedVertices(
      this.currentSelection.faces
        .map((x) => this.asset.designNode.getVerticesByFaceId(x, 'world'))
        .flat(),
    );
    this.selectedFaces.visible = true;
  }

  onKeyUp(input?: KeyInputData) {
    if (input) {
      this.onModifierChanged(input);
    }

    if (!this.inputHandler.shiftIsDown && !this.inputHandler.ctrlIsDown) {
      this.inputHandler.setCursor(this.toolCursor);
    } else if (this.inputHandler.ctrlIsDown) {
      this.inputHandler.setCursor('copy');
    } else if (this.inputHandler.shiftIsDown) {
      this.inputHandler.setCursor('default');
    }

    if (this.mousePosition) {
      this.updateTooltip();
    }
  }

  get multiSelectInProgress() {
    return (
      this.currentSelection.points.length > 0 ||
      this.currentSelection.lineSegments.length > 0 ||
      this.currentSelection.faces.length > 0
    );
  }

  /**
   * Alt and Shift change what is being drawn, e.g. a line to a curve, which is shown right away
   * rather than when the mouse moves. Alt does nothing else in the browser then, such as showing
   * its menu.
   */
  private onModifierChanged(input: KeyInputData) {
    if (!this.actionInProgress || (input.key !== 'Alt' && input.key !== 'Shift')) {
      return;
    }

    if (input.key === 'Alt') {
      input.e.preventDefault();
    }

    this.onMouseMove(this.lastMouseEvent);
  }

  onKeyDown(input: KeyInputData) {
    // Typing in the create point dialogs should not trigger any hotkeys, e.g. Delete or Escape.
    if (
      this.uiState === DesignToolDialogState.CreatePointByCoordinate ||
      this.uiState === DesignToolDialogState.CreatePointByOffset
    ) {
      return;
    }

    this.onModifierChanged(input);

    this.inputHandler.handleKeyPress(input, 'Control', () => {
      this.inputHandler.setCursor('copy');
    });
    this.inputHandler.handleKeyPress(input, 'Shift', () => {
      if (!input.ctrl) {
        this.inputHandler.setCursor('default');
      }
    });

    this.inputHandler.handleKeyPress(
      input,
      's',
      () => {
        commands.saveTool(this);
      },
      'Control',
    );

    if (this.actionInProgress) {
      this.inputHandler.handleKeyPress(input, 'Escape', () => {
        void commands.abortActionInProgress(this).action();
      });
    }

    if (this.currentPoint) {
      this.inputHandler.handleKeyPress(
        input,
        'c',
        () => {
          void this.getCopyCoordinateCommand(
            this.intersection?.point ?? this.currentPoint,
          )[0]?.action();
        },
        'Control',
      );
    }

    this.inputHandler.handleKeyPress(input, 'q', () => {
      this.designMode = DesignMode.Line;
    });
    this.inputHandler.handleKeyPress(input, 'w', () => {
      this.designMode = DesignMode.Point;
    });
    this.inputHandler.handleKeyPress(input, 'e', () => {
      this.designMode = DesignMode.Select;
    });
    this.inputHandler.handleKeyPress(input, 'r', () => {
      this.designMode = DesignMode.Text;
    });

    if (this.designCurves) {
      this.inputHandler.handleKeyPress(input, 'u', () => {
        this.designMode = DesignMode.Curve;
      });

      // Not with Control, which copies the coordinate.
      if (!input.ctrl && !input.metaKey) {
        this.inputHandler.handleKeyPress(input, 'c', () => {
          this.designMode = DesignMode.Circle;
        });
      }
    }

    this.inputHandler.handleKeyPress(
      input,
      'a',
      () => {
        void commands.selectAll(this).action();
      },
      'Control',
    );

    this.inputHandler.handleKeyPress(input, 'o', () => {
      void commands.activateCreateOffsetFeature(this).action();
    });

    this.inputHandler.handleKeyPress(input, 'h', () => {
      void commands.activateSetPointHeightsFeature(this).action();
    });

    this.inputHandler.handleKeyPress(input, 't', () => {
      void commands.activateTriangualateFeature(this).action();
    });

    this.inputHandler.handleKeyPress(input, 'i', () => {
      void commands.activateInterpolatePointHeightsFeature(this).action();
    });

    this.inputHandler.handleKeyPress(input, 'g', () => {
      void commands.activateGenerateGridFeature(this).action();
    });

    this.inputHandler.handleKeyPress(input, 'l', () => {
      void commands.activateGenerateLineSegmentsFeature(this).action();
    });

    // Dialogs for creating points from a coordinate or an offset
    if (!this.actionInProgress || this.actionInProgress.actionName === ActionName.CreateLine) {
      this.inputHandler.handleKeyPress(input, 'k', () => {
        void commands.openCreatePointByCoordinateDialog(this).action();
      });
      this.inputHandler.handleKeyPress(input, 'x', () => {
        void commands.openCreatePointByOffsetDialog(this).action();
      });
    }

    if (this.multiSelectInProgress) {
      this.inputHandler.handleKeyPress(input, 'Escape', () => {
        void commands.deselectAll(this).action();
      });
      this.inputHandler.handleKeyPress(input, 'Delete', () => {
        void commands.deleteSelectedGeometry(this).action();
      });
      return;
    }

    // Points can also be placed while creating a line, without creating the line segment
    if (
      this.currentPoint &&
      (!this.actionInProgress || this.actionInProgress.actionName === ActionName.CreateLine)
    ) {
      this.inputHandler.handleKeyPress(input, 'p', () => {
        void commands.createPoint(this).action();
      });
    }

    // These commands are only possible to do when there is no ongoing action
    if (!this.actionInProgress) {
      this.inputHandler.handleKeyPress(
        input,
        'z',
        () => {
          void commands.undo(this).action();
        },
        'Control',
      );
      this.inputHandler.handleKeyPress(
        input,
        'y',
        () => {
          void commands.redo(this).action();
        },
        'Control',
      );

      if (this.intersection) {
        const { isPointHighlighted, isFaceHighlighted, isLineHighlighted } =
          this.getSelectionType();

        this.inputHandler.handleKeyPress(input, 'm', () => {
          if (this.hoveredCircleHandle) {
            void commands.resizeCircle(this, this.hoveredCircleHandle).action();
          } else if (isPointHighlighted) {
            void commands.movePoint(this).action();
          } else if (isDefined(this.hoveredCurveHandle)) {
            void commands.bendLine(this, this.hoveredCurveHandle).action();
          }
        });

        this.inputHandler.handleKeyPress(input, 'Delete', () => {
          if (this.hoveredCircleHandle) {
            void commands
              .deleteLine(this, this.hoveredCircleHandle.circle.lineSegments[0])
              .action();
          } else if (isPointHighlighted) {
            void commands.deletePoint(this).action();
          } else if (isLineHighlighted) {
            void commands.deleteLine(this).action();
          } else if (isFaceHighlighted) {
            void commands.deleteFace(this).action();
          }
        });
      }
    }
  }

  onMouseWheel(e: MouseInputData) {
    this.contextMenuCallback?.();
    this.placePointer(e);

    if (this.actionInProgress) {
      this.actionInProgress?.refresh?.();
    }

    return true;
  }

  onProjectProjectionUpdated() {
    const origoDifference = CoordinateManager.getInstance().origoDifference;

    this.currentPoint?.add(origoDifference);
    this.actionInProgress?.onProjectProjectionUpdated?.(origoDifference);
    this.selectedPoints.position.add(origoDifference);
    this.selectedLineSegments.position.add(origoDifference);
    this.selectedFaces.position.add(origoDifference);

    return true;
  }

  enable() {
    this.visible = true;
    this.inputHandler.setCursor(this.toolCursor);
    this.reset();
  }

  openSaveConfirmationDialog(toolNames: string) {
    this.uiState = DesignToolDialogState.Abort;
  }

  getDefaultGeometry() {
    const asset = new Design({
      name: getNextDefaultName('design'),
      visible: true,
      MAS: undefined,
    });

    const { valueMap, colorMap } = getDefaultColorValueMap();

    asset.designNode.addAttribute({
      name:
        i18next?.t('section.attributes.defaultAttributeName', { ns: 'skyviewDesign' }) ?? 'Färg',
      id: 0 as AttributeId,
      colorMap,
      valueMap,
      source: 'skymap-design',
      type: 'enum',
    });

    return asset;
  }

  async disable() {
    this.contextMenuCallback?.();
    deselect(this);
    this.visible = false;
    this.cursorLine.visible = false;
    this.cursorFace.visible = false;
    this.centerHighlight.visible = false;
    this.heightsGuide.hide();
    this.inputHandler.setCursor('');
    AssetManager.instance.removeTemp(this.asset, {
      dispose: true,
    });
    this.actionInProgress?.cleanup?.();
    this.reset();

    return Promise.resolve();
  }

  reset() {
    this.actionInProgress?.cleanup?.();
    this.actionInProgress = undefined;
    this.activeAttributeGroupRowId = undefined;
    this.activeAttributeGroup = undefined;
    this.pointHeightInterpolationMode = 'interpolateStartEnd';
    this.pointHeightInterpolationSlope = 0;

    this.uiState = DesignToolDialogState.None;
    this._activeTool = DesignMode.Line;
    this.activeFeature = DesignFeature.SetPointHeights;
    this.saved = true;
    this.mouseInView = false;
    this.autoTriggerOffset = false;
    this.applyAttributesOnNewPoints = false;
    this.activeAttributes.clear();
    this.toolCursor = 'crosshair';
    this.actions = [];
    this.undoActions = [];
    this.selectedPoints.visible = false;
    this.selectedLineSegments.visible = false;
    this.selectedFaces.visible = false;

    this.intersection = undefined;
    this.asset.locked = false;
    this.asset.setPointSize(false);
    this.asset.materials.points.needsUpdate = true;
    this.asset.toggleDoubleSided(DoubleSide);

    this.asset = this.getDefaultGeometry();
    this.currentPoint = undefined;
    this.snapValues = getDefaultSnapValues();
    this.snapSettings = getDefaultSnapSettings();
    this.excludedSnapAssetIds = new Set();
    this.clearTooltip();
    this.clearContextMenu();
  }

  updateTooltip(screenPoint?: Point2d) {
    const point = this.currentPoint ?? this.actionInProgress?.tooltipPoint?.();

    if (!point || !this.mouseInView) {
      return;
    }

    const position = screenPoint ?? this.lastMouseEvent.pointer.client;

    if (!position) {
      return;
    }

    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    const coords = CoordinateManager.getInstance().localToProjectCoords(point);

    publish(SubscriptionTopic.ViewerCursorPositionChanged, coords);
    this.showTooltip({
      ...buildTooltipProps(
        this.asset,
        camera,
        coords,
        {
          x: position.x,
          y: position.y,
        },
        this,
      ),
      heights: this.heightsGuide.getTooltipHeights(this.currentPoint),
      snapSettings: this.getTooltipSnapSettings(),
    });
  }

  /**
   * The snap settings of what is snapped, for the tooltip, with the ones that are not used marked,
   * see getSnapSettingsStatus: a point, the next point of a line, or an offset. None while the
   * option to show them is off, or for other actions, e.g. moving a point.
   */
  private getTooltipSnapSettings(): TooltipSnapSettings | undefined {
    if (!this.tooltipSnapSettings) {
      return undefined;
    }

    const action = this.actionInProgress;
    const snapAction: SnapAction | undefined = !action
      ? 'point'
      : action.actionName === ActionName.Offset
        ? 'offset'
        : this.lineOrigin
          ? 'line'
          : undefined;
    const settings =
      snapAction && getSnapSettingsStatus(this.snapSettings, this.snapValues, snapAction);

    return snapAction && settings && settings.length > 0
      ? { action: snapAction, settings }
      : undefined;
  }

  getCopyCoordinateCommand = (point?: Vector3) => {
    return option(isDefined(point), () => {
      const coords = CoordinateManager.getInstance().localToProjectCoords(point!);
      return copyCoordinate(coordinateToPoint3d(coords), this.contextMenuCallback);
    });
  };

  getSelectionType() {
    return {
      isPointHighlighted: this.intersection?.object instanceof Points,
      isLineHighlighted: isLineSegmentObject(this.intersection?.object),
      // Styled lines are drawn by meshes too.
      isFaceHighlighted:
        this.intersection?.object instanceof Mesh && !isStyledLines(this.intersection.object),
    };
  }

  updateContextMenu(e: MouseInputData) {
    this.onEdited?.();
    let items: ContextMenuItem[];

    const { isPointHighlighted, isFaceHighlighted, isLineHighlighted } = this.getSelectionType();

    const copyCommand = this.getCopyCoordinateCommand(
      this.intersection?.point ?? this.currentPoint,
    );

    if (this.actionInProgress) {
      /**
       * Create line should only have an affirmative abort command so the user is not confused
       * by multiple options.
       */
      if (
        this.actionInProgress.actionName === ActionName.CreateLine ||
        this.actionInProgress.actionName === ActionName.CreateCurve
      ) {
        items = [
          commands.abortActionInProgressAffirmative(this),
          commands.createActions(this),
          ...(this.actionInProgress.getExtraOptions?.(e) ?? []),
        ];
      } else {
        items = [
          commands.applyActionInProgress(this),
          commands.abortActionInProgress(this),
          ...(this.actionInProgress.getExtraOptions?.(e) ?? []),
        ];
      }
    } else if (this.multiSelectInProgress) {
      items = [
        ...option(isPointHighlighted, () => commands.startPolylineSelection(this)),
        ...option(isLineHighlighted, () => commands.selectLineOrPolygon(this)),
        commands.deselectAll(this),
        commands.deleteSelectedGeometry(this),
        ...option(this.currentSelection.faces.length > 0, () => commands.flipFace(this)),
      ];
    } else if (isPointHighlighted) {
      items = [
        commands.startPolylineSelection(this),
        commands.movePoint(this),
        commands.deletePoint(this),
        commands.activateSetPointHeightsFeature(this),
        commands.createActions(this),
      ];
    } else if (isLineHighlighted) {
      const line = this.asset.designNode.getLineSegmentByIndex(this.intersection?.index!);
      const connectedFaces = isDefined(line)
        ? getLineSegmentFaces(this.asset.designNode, line.id)
        : [];

      items = [
        commands.selectLineOrPolygon(this),
        commands.deleteLine(this),
        ...option(connectedFaces.length === 2, () => commands.flipBreakline(this, connectedFaces)),
        ...option(this.designCurves, () => commands.bendLine(this, line.id)),
        ...option(this.designCurves && isDefined(line.curve), () =>
          commands.straightenLine(this, line.id),
        ),
        commands.createActions(this),
      ];
    } else if (isFaceHighlighted) {
      items = [commands.deleteFace(this), commands.flipFace(this)];
    } else {
      items = [commands.undo(this), commands.redo(this), commands.createActions(this)];
    }

    // Options that will be available no matter the context.
    items.push(
      commands.tools(this),
      commands.showFunctions(this),
      ...copyCommand,
      commands.showOptions(this),
      commands.abortTool(this),
    );

    this.contextMenuCallback?.({
      pos: {
        x: e.viewportPos?.clientX ?? 0,
        y: e.viewportPos?.clientY ?? 0,
      },
      items,
    });
  }

  clearTooltip() {
    publish(SubscriptionTopic.ViewerCursorPositionChanged, null);
    this.hideTooltip();
  }

  /**
   * Shows the tooltip in the right-side menu when it is placed there, otherwise next to the cursor.
   */
  private showTooltip(tooltip: SkyviewAssetPositionProps) {
    showToolTooltip(this, tooltip);
  }

  private hideTooltip() {
    showToolTooltip(this);
  }
  clearContextMenu() {
    this.contextMenuCallback?.();
  }
}
